<template>
    <div>
        <div class="mt-5 flex flex-col md:flex-row justify-arrow">
            <div class="flex flex-col items-center md:items-start w-full">
                <h1 class="text-3xl text-center md:text-left">
                    {{ id ? "" : "Nueva Tienda" }}
                    <strong v-if="id" class="text-theme-primary">
                        {{ itemComponent.name }}
                        {{
                            itemComponent.isOwn && $hasInternalCommissions
                                ? "(Propia)"
                                : ""
                        }}
                    </strong>
                </h1>
                <div v-if="id" class="flex items-center gap-1">
                    <strong class="text-sm text-theme-muted">ID</strong>
                    <uuid-display :value="itemComponent.id" />
                </div>
            </div>
            <div
                v-if="id"
                class="flex flex-col mt-2 md:mt-0 md:min-w-max justify-center text-center"
            >
                <small> 📆💾 {{ itemComponent.createdAt | date }} </small>
                <small> 📆✏ {{ itemComponent.updatedAt | date }} </small>
            </div>
        </div>

        <custom-navbar
            v-if="id"
            id="tabs-products"
            v-model="activeTab"
            center-collapsed
            not-line
            class="my-4"
        >
            <template #left>
                <custom-navbar-item id="home">
                    <router-link
                        to="/app/stores"
                        class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                    >
                        <span>🏠</span>
                    </router-link>
                </custom-navbar-item>

                <custom-navbar-item
                    id="general"
                    :active="['general', 'home'].includes(activeTab)"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> General </span>
                        <span>&nbsp;⚙</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="$ability.hasPermission('store:GetMoney')"
                    id="money"
                    :active="activeTab === 'money'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Dinero </span>
                        <span>&nbsp;💰</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="$ability.hasPermission('store:GetServices')"
                    id="services"
                    :active="activeTab === 'services'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Servicios </span>
                        <span>&nbsp;👨‍🏭</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="$ability.hasPermission('user:ListOwn')"
                    id="users"
                    :active="activeTab === 'users'"
                    class="px-2 md:px-4 xl:px-6"
                    @click="listMerchantsUserComponent"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Usuarios </span>
                        <span>&nbsp;👩‍🔧</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="$enabledEcommerceOptions"
                    id="seo"
                    :active="activeTab === 'seo'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Contenido&nbsp; </span>
                        <span>&nbsp;📒</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    id="advance"
                    :active="activeTab === 'advance'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Avanzado&nbsp; </span>
                        <span>🤖</span>
                    </div>
                </custom-navbar-item>
            </template>
            <template #right>
                <custom-switch
                    v-if="$ability.hasPermission('store:Update')"
                    v-model="enabledUpdate"
                >
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </template>
        </custom-navbar>

        <div v-show="['general', 'home'].includes(activeTab)">
            <div v-if="id" class="flex flex-wrap">
                <div
                    class="w-full md:w-1/2 mt-4 flex justify-center items-center"
                >
                    <div
                        v-if="
                            $enabledEcommerceOptions &&
                            $ability.hasPermission('store:UpdateActive')
                        "
                        class="flex items-center gap-2"
                    >
                        <span class="text-sm font-medium text-theme-base">
                            Habilitado para entregas
                        </span>
                        <custom-switch
                            v-if="enabledUpdate"
                            v-model="itemComponent.enabledForDeliveries"
                            success
                            @change="updateActiveComponent"
                        >
                            <template #on><small>Si</small></template>
                            <template #off><small>No</small></template>
                        </custom-switch>
                        <span
                            v-else
                            class="text-sm font-semibold"
                            :class="
                                itemComponent.enabledForDeliveries
                                    ? 'text-theme-success'
                                    : 'text-theme-danger'
                            "
                        >
                            {{
                                itemComponent.enabledForDeliveries ? "Si" : "No"
                            }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="flex flex-wrap">
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.name"
                        label="Nombre"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidName & introValidators.name
                                ? 'Nombre de tienda invalido'
                                : ''
                        "
                        @blur="setSlug"
                    />
                </div>
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.slug"
                        label="Slug"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidSlug && introValidators.slug
                                ? 'Slug invalido'
                                : ''
                        "
                        @blur="introValidators.slug = true"
                    />
                </div>
            </div>

            <div class="flex flex-wrap">
                <fragment v-if="$enabledEcommerceOptions">
                    <div class="w-full md:w-1/2 px-2">
                        <custom-input
                            v-model="itemComponent.summary"
                            label="Descripción"
                            :disabled="!enabledUpdate"
                        />
                    </div>
                </fragment>

                <div class="w-full md:w-1/2 mt-6 px-2">
                    <city-code-selector
                        v-model="itemComponent.cityCode"
                        :enabled="enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.address"
                        class="w-full"
                        label="Dirección Ej. Cll 42 # 12 - 8"
                        placeholder="Cll 42 # 12 - 8"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidAddress & introValidators.address
                                ? 'Dirección invalida'
                                : ''
                        "
                        @blur="onBlurAddress"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.addressComplement"
                        class="w-full"
                        label="Complemento de Dirección Ej. Local 123"
                        placeholder="Local 123"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.neighborhood"
                        class="w-full"
                        label="Barrio / Localidad"
                        :disabled="!enabledUpdate"
                    />
                </div>
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.phone"
                        class="w-full"
                        label="Celular de contacto"
                        type="number"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidPhone & introValidators.phone
                                ? 'Celular invalido'
                                : ''
                        "
                        @blur="introValidators.phone = true"
                    />
                </div>
                <fragment v-if="$enabledEcommerceOptions">
                    <div class="w-full md:w-1/2 px-2">
                        <custom-input
                            v-model="itemComponent.identificationNumber"
                            class="w-full"
                            label="Nit (Optional)"
                            :disabled="!enabledUpdate"
                        />
                    </div>
                    <div class="w-full md:w-1/2 mt-6 px-2">
                        <coordinates-selector
                            v-model="itemComponent.coordinates"
                            :enabled="enabledUpdate"
                        />
                    </div>
                </fragment>
            </div>

            <div class="flex flex-wrap mt-4">
                <images-manage
                    :id="`${id}`"
                    v-model="images"
                    type="tienda"
                    :enabled="enabledUpdate"
                    :default-alt="'Imagen de ' + itemComponent.name"
                    default-size="squareImage"
                />
            </div>

            <div class="flex flex-wrap mt-4">
                <div v-if="enabledUpdate" class="w-full flex justify-center">
                    <app-button
                        v-if="!id"
                        size="large"
                        @click="createItemComponent"
                    >
                        Crear tienda
                    </app-button>
                    <app-button
                        v-else
                        size="large"
                        @click="updateItemComponent"
                    >
                        Actualizar tienda
                    </app-button>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'money'">
            <div class="flex mt-4 mx-3">
                En esta sección puedes actualizar el dinero que esta en la
                caja.Ten cuidado de no hacerlo en un horario donde haya mucho
                flujo de trasacciones ya que puedes eliminar el dinero ingresado
                por una trasacción accidentalmente.
            </div>
            <money-config-manage
                v-model="itemComponent.moneyConfig"
                :enabled="
                    $ability.hasPermission('store:UpdateMoney') && enabledUpdate
                "
            />
            <div class="flex flex-wrap mt-4">
                <div v-if="enabledUpdate" class="w-full flex justify-center">
                    <app-button @click="updateMerchantMoneyComponent">
                        Actualizar dinero en tienda
                    </app-button>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'services'">
            <services-in-merchant-manage
                v-model="itemComponent.services"
                :enabled="
                    $ability.hasPermission('store:UpdateServices') &&
                    enabledUpdate
                "
                class="mt-4"
            />
            <div class="flex flex-wrap mt-4">
                <div v-if="enabledUpdate" class="w-full flex justify-center">
                    <app-button @click="updateServicesMerchantComponent">
                        Actualizar servicios
                    </app-button>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'users'">
            <div v-if="isLoadingUsers" class="w-full flex justify-center my-4">
                <span>Cargando...</span>
            </div>
            <div v-else>
                <users-roles-manage
                    v-model="users"
                    :enabled="
                        $ability.hasPermission('user:UpdateOwn') &&
                        enabledUpdate
                    "
                    :default-filters="{ type: 'merchant' }"
                />
                <div class="flex flex-wrap mt-4">
                    <div
                        v-if="enabledUpdate"
                        class="w-full flex justify-center"
                    >
                        <app-button @click="updateUsersMerchantComponent">
                            Actualizar usuarios
                        </app-button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'seo'" id="seoTab" class="space-y-6">
            <div class="flex flex-col">
                <h2 class="text-theme-primary text-2xl font-semibold my-4">
                    Contenido de la tienda
                </h2>
                <span>
                    Este contenido es el que se va a mostrar en la página de la
                    tienda, es importante que esté bien redactado y sea
                    atractivo para los usuarios, ya que esto va a influir en la
                    decisión de compra de los usuarios.
                </span>
            </div>

            <div class="bg-theme-card rounded-2xl shadow-sm p-5 mt-5">
                <h2 class="text-theme-primary text-xl font-semibold mb-4">
                    Contenido de la tienda
                </h2>
                <div v-if="$enabledEcommerceOptions">
                    <markdown-editor
                        v-model="itemComponent.content"
                        class="mt-2"
                        :enabled="enabledUpdate"
                    />
                </div>
            </div>

            <div
                v-if="$enabledEcommerceOptions"
                class="bg-theme-card rounded-2xl shadow-sm p-5"
            >
                <h2 class="text-theme-primary text-xl font-semibold mb-4">
                    Palabras clave
                </h2>
                <keywords-selector
                    v-model="itemComponent.keywords"
                    :enabled="enabledUpdate"
                />
            </div>

            <div v-if="enabledUpdate" class="w-full flex justify-center">
                <app-button
                    :loading="isLoadingContent"
                    @click="updateContentComponent"
                >
                    Actualizar Contenido
                </app-button>
            </div>
        </div>

        <div v-if="activeTab === 'advance'" id="advanceTab">
            <div id="title-routes" class="flex flex-wrap">
                <div class="flex flex-col">
                    <h2 class="text-theme-primary text-2xl">Avanzado</h2>
                    <span>
                        En esta sección prodrás activar o desactivar inventario,
                        sincronizar precios o eliminar la tienda aqui, ten mucho
                        cuidado.
                    </span>
                </div>
            </div>
            <div
                v-if="id"
                class="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
                <app-button
                    v-if="enabledUpdate"
                    @click="selectMerchantComponent"
                >
                    Selecciona tienda para operaciones
                </app-button>
                <app-button
                    v-if="
                        $enabledEcommerceOptions &&
                        $ability.hasPermission('store:Transfer')
                    "
                    @click="transferComponent"
                >
                    Establecer como
                    {{ itemComponent.isOwn ? "proveedor" : "sucursal" }}
                </app-button>
                <app-button
                    v-if="$ability.hasPermission('inventory:SyncPrices')"
                    @click="syncPricesInventoryComponent"
                >
                    Sincronizar precios con {{ $siteType }}
                </app-button>
                <app-button
                    v-if="$ability.hasPermission('inventory:Active')"
                    @click="activeInventoryComponent"
                >
                    Activar todo el inventario
                </app-button>

                <app-button
                    v-if="$ability.hasPermission('inventory:Active')"
                    warn
                    @click="disableInventoryComponent"
                >
                    Desactivar todo el inventario
                </app-button>
                <app-button
                    v-if="$ability.hasPermission('store:Delete')"
                    danger
                    @click="deleteItemComponent"
                >
                    Eliminación
                </app-button>
                <app-button
                    v-if="$ability.hasPermission('store:DeleteDefinitive')"
                    danger
                    @click="deleteItemDefinitelyComponent"
                >
                    Eliminación definitiva
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import planLimitError from "@/mixins/planLimitError";
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters, mapMutations } from "vuex";

import { EMPTY_MONEY_CONFIG } from "@/common/lib/money";
import {
    addressRegex,
    phoneRegex,
    slugRegex,
    titleRegex
} from "@/common/lib/regex";
import {
    removeEmptyFields,
    slugify,
    validateConfirmationMessage
} from "@/common/lib/transformers";

import UsersRolesManage from "@/components/users/UsersRolesManage.vue";
import CityCodeSelector from "@/components/utils/CityCodeSelector.vue";
import CoordinatesSelector from "@/components/utils/CoordinatesSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImagesManage from "@/components/utils/ImagesManage.vue";
import KeywordsSelector from "@/components/utils/KeywordsSelector.vue";
import MarkdownEditor from "@/components/utils/MarkdownEditor.vue";
import MoneyConfigManage from "@/components/utils/MoneyConfigManage.vue";
import ServicesInMerchantManage from "@/components/utils/ServicesInMerchantManage.vue";
import UuidDisplay from "@/components/utils/UuidDisplay.vue";

export default {
    name: "StoreDetail",
    components: {
        ImagesManage,
        MarkdownEditor,
        UsersRolesManage,
        KeywordsSelector,
        CoordinatesSelector,
        CityCodeSelector,
        MoneyConfigManage,
        ServicesInMerchantManage,
        CustomInput,
        UuidDisplay
    },
    mixins: [tabQuerySyncMixin, planLimitError],
    data: () => ({
        activeTab: "general",
        $tabsWhitelist: [
            "general",
            "money",
            "services",
            "users",
            "seo",
            "advance"
        ],
        id: "",
        name: "",
        isLoading: false,
        isLoadingContent: false,
        isLoadingUsers: false,
        enabledUpdate: false,
        loadedAllData: false,
        users: [],
        images: [],
        itemComponent: {
            id: "",
            isOwn: false,
            name: "",
            slug: "",
            identificationNumber: "",
            identificationType: "",
            content: "",
            enabledForDeliveries: false,
            summary: "",
            phone: "",
            coordinates: { latitude: 4.63082, longitude: -74.15035 },
            moneyConfig: EMPTY_MONEY_CONFIG,
            address: "",
            cityCode: "11001",
            keywords: [],
            neighborhood: "",
            addressComplement: "",
            weight: 120,
            telegramChannelId: "",
            images: []
        },
        introValidators: {
            phone: false,
            address: false,
            name: false,
            slug: false
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isValidAddress() {
            return (
                !this.itemComponent.address ||
                addressRegex.test(this.itemComponent.address)
            );
        },
        isValidPhone() {
            return (
                !this.itemComponent.phone ||
                phoneRegex.test(this.itemComponent.phone)
            );
        },
        isValidName() {
            return titleRegex.test(this.itemComponent.name);
        },
        isValidSlug() {
            return slugRegex.test(this.itemComponent.slug);
        }
    },
    watch: {
        "$route.params.id"(newId, oldId) {
            if (!newId || newId === oldId) return;
            this.id = newId;
            this.getBaseItem(newId);
        },
        "$route.params.idClone"(newId, oldId) {
            if (!newId || newId === oldId) return;
            this.getBaseItem(newId);
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if ("id" in this.$route.params) {
            if (!(await verifyPagePermission("store:Get", "/app/stores")))
                return;
            const id = this.$route.params.id;
            this.id = id;
            await this.getBaseItem(id);
        } else if ("idClone" in this.$route.params) {
            if (!(await verifyPagePermission("store:Create", "/app/stores")))
                return;
            this.id = "";
            this.enabledUpdate = true;
            const idTmp = this.$route.params.idClone;
            await this.getBaseItem(idTmp);
            this.itemComponent = {
                ...this.itemComponent,
                name: `${this.itemComponent.name} CLONE`,
                slug: `${this.itemComponent.slug}-clone`,
                enabledForDeliveries: false
            };
            this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas clonando una tienda",
                icon: "info",
                showCloseButton: true
            });
        } else {
            await verifyPagePermission("store:Create", "/app/stores");
            this.enabledUpdate = true;
            this.loadedAllData = true;
        }
    },
    methods: {
        ...mapMutations("commons", ["SET_MERCHANT_SELECTED"]),
        ...mapActions("auth", ["applyMerchantPermissions"]),
        ...mapActions("stores", [
            "getStoreById",
            "updateStore",
            "deleteStore",
            "deleteStoreDefinitively",
            "createStore",
            "updateStoreMoney",
            "setStoreActive",
            "transferStore",
            "updateStoreServices"
        ]),
        ...mapActions("inventories", [
            "syncPricesInventory",
            "activeAllInventories",
            "disableAllInventories"
        ]),
        ...mapActions("users", ["listUsersMerchant", "updateUsersMerchant"]),
        async getBaseItem(id) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.getStoreById({ id });
                this.name = data.name;
                this.itemComponent = {
                    ...data,
                    ...data.communicationConfig,
                    services: data.services.map((item) => ({
                        priceOffer: item.priceOffer,
                        name: item.product.name
                    }))
                };
                this.images = [...this.itemComponent.images];
                this.loadedAllData = true;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/stores");
                }
            }
        },
        async listMerchantsUserComponent() {
            this.isLoadingUsers = true;
            this.$loading.show();
            try {
                this.users = await this.listUsersMerchant({
                    merchantId: this.id,
                    merchantType: "store"
                });
                this.isLoadingUsers = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoadingUsers = false;
                this.$loading.hide();
                this.isLoadingUsers = false;
                this.$loading.hide();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateActiveComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.setStoreActive(this.itemComponent);
                this.$sound.play();
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.itemComponent.enabledForDeliveries =
                    !this.itemComponent.enabledForDeliveries;
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateItemComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateStore(this.getDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async createItemComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.createStore(this.getDataSend());
                this.$sound.play();
                this.$loading.hide();
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `Id: ${data.id}`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista de tiendas",
                    cancelButtonText: "Nueva tienda"
                });
                if (dialogResult.isConfirmed) {
                    this.id = data.id;
                    this.itemComponent = { ...data, ...this.itemComponent };
                    this.$router.push(`/app/stores/detail/${this.id}`);
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/stores/");
                }
                if (dialogResult.isDismissed) {
                    this.introValidators = {
                        phone: false,
                        address: false,
                        name: false,
                        slug: false
                    };
                    this.itemComponent = {
                        id: "",
                        name: "",
                        slug: "",
                        enabledForDeliveries: false,
                        content: "",
                        summary: "",
                        phone: "",
                        coordinates: {
                            latitude: 4.63082,
                            longitude: -74.15035
                        },
                        moneyConfig: EMPTY_MONEY_CONFIG,
                        address: "",
                        cityCode: "11001",
                        keywords: [],
                        neighborhood: "",
                        addressComplement: "",
                        weight: 120
                    };
                }
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                if (this.openPlanUpgradeForLimit(error)) return;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/stores");
                }
            }
        },
        async updateMerchantMoneyComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateStoreMoney({
                    id: this.itemComponent.id,
                    moneyConfig: this.itemComponent.moneyConfig
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateServicesMerchantComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateStoreServices({
                    id: this.itemComponent.id,
                    services: this.itemComponent.services.map(
                        (item, index) => ({
                            priceOffer: item.priceOffer,
                            product: {
                                name: item.name
                            },
                            quantity: 1,
                            id: index + 1
                        })
                    )
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateUsersMerchantComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateUsersMerchant({
                    merchantType: "store",
                    merchantId: this.id,
                    users: this.users.map((item) => ({
                        email: item.email,
                        roleSlug: item.role.slug
                    }))
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async setSlug() {
            this.introValidators.name = true;
            if (
                this.enabledUpdate &&
                this.isValidName &&
                !this.itemComponent.enabledForDeliveries
            ) {
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Quieres cambiar el slug ?",
                    icon: "info",
                    showCancelButton: true,
                    confirmButtonColor: "#02913f",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: "Si",
                    cancelButtonText: "No"
                });
                if (dialogResult.isConfirmed) {
                    this.itemComponent.slug = slugify(this.itemComponent.name);
                }
            }
        },
        async deleteItemComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: `Estas seguro deseas eliminar la tienda ?`,
                text: "Las tiendas solo deben eliminarse en caso de que se esten haciendo pruebas, eres consiente de lo que estas haciendo ?. Escribe 'Si' para eliminar",
                icon: "warning",
                input: "text",
                confirmButtonColor: "#ee3f59",
                cancelButtonColor: "#253C99",
                showCancelButton: true
            });
            if (
                dialogResult.isConfirmed &&
                validateConfirmationMessage(dialogResult.value)
            ) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteStore({
                        id: this.id
                    });
                    this.$router.push("/app");
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async deleteItemDefinitelyComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: `Estas seguro`,
                text: `Las tiendas solo deben eliminarse en caso de que se esten haciendo pruebas, eres consiente de lo que estas haciendo ?. Escribe 'Si' para eliminar`,
                icon: "warning",
                input: "text",
                confirmButtonColor: "#ee3f59",
                cancelButtonColor: "#253C99",
                showCancelButton: true
            });
            if (
                dialogResult.isConfirmed &&
                validateConfirmationMessage(dialogResult.value)
            ) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteStoreDefinitively({
                        id: this.id
                    });
                    this.$router.push("/app");
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async syncPricesInventoryComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Vas a sincronizar los precios de tu inventario ?",
                text: `Quieres que TODOS los precios del tienda${this.itemComponent.name} tendrá el mismo precio de la referencia en el ${this.$siteType}. Para reestablecer un precio debes entrar el detalle de la referencia.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.syncPricesInventory({
                        merchantType: "store",
                        merchantId: this.itemComponent.id
                    });
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Actualización exitosa",
                        icon: "success",
                        showCloseButton: true
                    });
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async transferComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: this.itemComponent.isOwn
                    ? "Vas a marcar la tienda como proveedor ?"
                    : "Vas a marcar la tienda como sucursal ?",
                text: this.itemComponent.isOwn
                    ? `A partir de ahora cada vez que ingreses un producto al inventario no estará habilitado por defecto para asignación de ordenes recibidas por el ${this.$siteType}.`
                    : `A partir de ahora tendrás disponible cada producto que ingreses en el inventario para asignación de ordenes recibidas por el ${this.$siteType}.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.transferStore({
                        id: this.itemComponent.id,
                        isOwn: !this.itemComponent.isOwn
                    });
                    this.itemComponent.isOwn = !this.itemComponent.isOwn;
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Actualización exitosa",
                        icon: "success",
                        showCloseButton: true
                    });
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async activeInventoryComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Vas a activar el inventario ?",
                text: `Quieres todos los productos en el inventario esten disponibles para ser comprados en el ${this.$siteType}.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.activeAllInventories({
                        merchantType: "store",
                        merchantId: this.itemComponent.id
                    });
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Actualización exitosa",
                        icon: "success",
                        showCloseButton: true
                    });
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async disableInventoryComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Vas a activar el inventario ?",
                text: `Quieres todos los productos en el inventario NO esten disponibles para ser comprados en el ${this.$siteType}.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.disableAllInventories({
                        merchantType: "store",
                        merchantId: this.itemComponent.id
                    });
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Actualización exitosa",
                        icon: "success",
                        showCloseButton: true
                    });
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        selectMerchantComponent() {
            const selectMerchant = {
                name: this.itemComponent.name,
                address: this.itemComponent.address,
                merchantType: "store",
                merchantId: this.itemComponent.id,
                moneyConfig: this.itemComponent.moneyConfig
            };
            this.SET_MERCHANT_SELECTED(selectMerchant);
            this.applyMerchantPermissions([]);
        },
        removeUser({ id }) {
            const index = this.users.findIndex((item) => item.id == id);
            let users = [...this.users];
            users.splice(index, 1);
            this.users = [...users];
        },
        validateData() {
            this.introValidators = {
                slug: true,
                name: true,
                phone: true,
                address: true
            };

            const toComplete = [];

            if (!this.isValidSlug) {
                toComplete.push("Slug");
            }

            if (!this.isValidName) {
                toComplete.push("Nombre");
            }

            if (!this.isValidPhone) {
                toComplete.push("Celular");
            }

            if (!this.isValidAddress) {
                toComplete.push("Dirección");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Tienda invalida",
                    text: `Revisa por favor revisa los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }
            return true;
        },
        getDataSend() {
            return removeEmptyFields({
                ...this.itemComponent,
                communicationConfig: {
                    telegramChannelId: this.itemComponent.telegramChannelId
                },
                images: this.images.map((item) => ({
                    id: item.id,
                    sequence: item.sequence.sequence
                })),
                id: this.id
            });
        },
        async updateContentComponent() {
            this.isLoadingContent = true;
            this.$loading.show();
            try {
                await this.updateStore({
                    id: this.id,
                    content: this.itemComponent.content,
                    keywords: this.itemComponent.keywords
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoadingContent = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoadingContent = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onBlurAddress() {
            this.introValidators.address = true;
            this.itemComponent.address = this.itemComponent.address.trim();
        }
    }
};
</script>
